import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';

import {
  ResponsiveNumber,
  RightBlock,
} from '#src/components/invite/OnlineLanding/RightBlock/index.tsx';

import type { CreditsEarned_currentUser } from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Credits earned',
  },
  subtitle: {
    defaultMessage: 'Use your credits to improve your teams',
  },
});

const NumberWrapper = styled(Vertical).attrs({ center: true })`
  width: 100%;
`;

const StyledResponsiveNumber = styled(ResponsiveNumber)`
  max-height: 200px;
`;

export const CreditsEarned = ({
  currentUser,
}: {
  currentUser?: CreditsEarned_currentUser | null;
}) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { main: totalEarned } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(
      currentUser?.totalReferralConversionCreditsEarned
    ),
    primaryCurrency: Currency.FIAT,
    hideFractionDigits: true,
  });
  const { main: potentialTotalEarned } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(
      currentUser?.potentialReferralConversionCredits
    ),
    primaryCurrency: Currency.FIAT,
    hideFractionDigits: true,
  });

  const hasEearnedCredits =
    (currentUser?.totalReferralConversionCreditsEarned.usdCents || 0) > 0;

  return (
    <RightBlock
      title={<FormattedMessage {...messages.title} />}
      subTitle={<FormattedMessage {...messages.subtitle} />}
      total={totalEarned}
      dialogProps={{
        title: <FormattedMessage {...messages.title} />,
        subtitle: hasEearnedCredits ? (
          <FormattedMessage {...messages.subtitle} />
        ) : (
          <FormattedMessage defaultMessage="No friends referred yet, no credits" />
        ),
        children: (
          <NumberWrapper>
            <StyledResponsiveNumber total={totalEarned} />
            <Horizontal>
              <BodyS color="var(--c-nd-600)">
                <FormattedMessage defaultMessage="pending" />
              </BodyS>
              <BodyS>{potentialTotalEarned}</BodyS>
            </Horizontal>
          </NumberWrapper>
        ),
      }}
      loading={!currentUser}
    />
  );
};

CreditsEarned.fragments = {
  currentUser: gql`
    fragment CreditsEarned_currentUser on CurrentUser {
      slug
      totalReferralConversionCreditsEarned(sport: $sport) {
        ...useMonetaryAmount_monetaryAmount
      }
      potentialReferralConversionCredits(sport: $sport) {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<CreditsEarned_currentUser>,
};
